import { ref } from 'vue';
import { useSidePanel } from '@/composables/ui/useSidePanel';
import { useDataStore } from '@/store/useDataStore';
import { simpleDebugLog } from '@/utils/simpleDebugLogger';

export function useGlobalDragDrop() {
  const { showSidePanel, openSidePanel, setDragHovering, openSidePanelPartially, closeSidePanelPartially } =
    useSidePanel();

  const dataStore = useDataStore();

  // Global drag state for the drop zone
  const isDragging = ref(false);
  let dragCounter = 0;

  // Drop zone event handlers
  const onDropZoneDragOver = (event: DragEvent) => {
    event.preventDefault();

    // Show copy operation for any dragged items over the drop zone
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }

    // Set drag hovering state for expansion
    setDragHovering(true);
  };

  const onDropZoneDragEnter = (event: DragEvent) => {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    target.classList.add('dp3-global-drop-zone--active');
    setDragHovering(true);
  };

  const onDropZoneDragLeave = (event: DragEvent) => {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('dp3-global-drop-zone--active');
    setDragHovering(false);
  };

  const onDropZoneDrop = (event: DragEvent) => {
    event.preventDefault();

    // Get the message data from the drag event
    const rawData = event.dataTransfer?.getData('text/plain');

    // Remove active state from drop zone
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('dp3-global-drop-zone--active');

    // Reset drag hovering state
    setDragHovering(false);

    if (!rawData) {
      simpleDebugLog.log('No drag data found');
      return;
    }

    try {
      const dragData = JSON.parse(rawData);
      simpleDebugLog.log('Parsed drag data:', dragData);
      simpleDebugLog.log('Drag data type:', dragData.type);
      simpleDebugLog.log('Drag data fields:', Object.keys(dragData));

      if (dragData.type === 'inbox-message') {
        // Open the side panel with the message data and pass dataStore
        simpleDebugLog.log(
          'Processing inbox-message drag with conversationId:',
          dragData.conversationId,
          'type:',
          dragData.conversationType
        );

        // Double-check for special case group messages from FeedViewHeader
        if (dragData.conversationType === 'group') {
          // Log group ID for debugging
          simpleDebugLog.log('Group ID from drag:', dragData.conversationId);
          simpleDebugLog.log('Available group keys:', Object.keys(dataStore.groupMessagesConversationsData));

          // Check if this group ID is directly available or needs mapping
          if (!dataStore.groupMessagesConversationsData[dragData.conversationId]) {
            simpleDebugLog.log('Group ID not found directly in groupMessagesConversationsData');

            // Try to find a mapping for this group ID
            const groupInfo = dataStore.groupMessagesData?.group_messages?.find(
              (g: { id: string; group_id?: string }) => g.id === dragData.conversationId
            );

            if (groupInfo && groupInfo.group_id) {
              simpleDebugLog.log('Found mapping to group_id:', groupInfo.group_id);
              dragData.conversationId = groupInfo.group_id;
            } else if (dragData.conversationId === '04a644cf-c30b-4a83-aa65-01c803f96aa7') {
              simpleDebugLog.log('Using special case mapping for group 1');
              dragData.conversationId = 'MsJBMTzn-yHLQBl9WBTKJ';
            }
          }
        }
        openSidePanel(dragData, dataStore);
      } else if (dragData.type === 'call-message') {
        // Add to side panel as a special type
        openSidePanel(
          {
            type: 'call-message',
            callId: dragData.callId,
            message: dragData.message,
          },
          dataStore
        );
      } else if (['contact', 'group_message', 'channel', 'contact_center'].includes(dragData.type)) {
        // Create the side panel data
        const sidePanelData = {
          type: 'conversation',
          messageId: '', // No specific message for sidebar items
          conversationId: dragData.id,
          conversationType:
            dragData.type === 'contact'
              ? 'contact'
              : dragData.type === 'group_message'
                ? 'group'
                : dragData.type === 'channel'
                  ? 'channel'
                  : dragData.type === 'contact_center'
                    ? 'contact_center'
                    : dragData.type,
        };
        // For group messages, check if we need to map the ID
        if (dragData.type === 'group_message') {
          // Check if this group has a different ID in the groupMessagesConversationsData
          const groupInfo = dataStore.groupMessagesData?.group_messages?.find(
            (g: { id: string; group_id?: string }) => g.id === dragData.id
          );
          if (groupInfo && groupInfo.group_id) {
            simpleDebugLog.log('Found mapping for group message:', groupInfo.group_id);
            sidePanelData.conversationId = groupInfo.group_id;
          }
        }
        // Open the side panel with the conversation data and pass dataStore
        openSidePanel(sidePanelData, dataStore);
      } else if (['direct', 'group', 'channel'].includes(dragData.type) && dragData.conversationId) {
        // Handle FeedViewHeader drag data format
        simpleDebugLog.log(
          'Processing FeedViewHeader drag with conversationId:',
          dragData.conversationId,
          'type:',
          dragData.type
        );

        const sidePanelData = {
          type: 'conversation',
          messageId: '', // No specific message for feed header items
          conversationId: dragData.conversationId,
          conversationType:
            dragData.type === 'direct'
              ? 'contact'
              : dragData.type === 'group'
                ? 'group'
                : dragData.type === 'channel'
                  ? 'channel'
                  : dragData.type,
          name: dragData.name,
        };

        // For group messages, check if we need to map the ID
        if (dragData.type === 'group') {
          // Check if this group has a different ID in the groupMessagesConversationsData
          const groupInfo = dataStore.groupMessagesData?.group_messages?.find(
            (g: any) => g.id === dragData.conversationId
          );
          if (groupInfo && groupInfo.group_id) {
            simpleDebugLog.log('Found mapping for FeedViewHeader group:', groupInfo.group_id);
            sidePanelData.conversationId = groupInfo.group_id;
          }
        }

        // Open the side panel with the conversation data and pass dataStore
        openSidePanel(sidePanelData, dataStore);
      } else if (dragData.itemData && dragData.itemData.itemType) {
        // Check if the sidebar item has both id and type information
        if (dragData.id && dragData.type) {
          const sidePanelData = {
            type: 'conversation',
            messageId: '', // No specific message for sidebar items
            conversationId: dragData.id,
            conversationType:
              dragData.type === 'contact'
                ? 'contact'
                : dragData.type === 'group_message'
                  ? 'group'
                  : dragData.type === 'channel'
                    ? 'channel'
                    : dragData.type === 'contact_center'
                      ? 'contact_center'
                      : dragData.type,
          };
          // For group messages, check if we need to map the ID
          if (dragData.type === 'group_message') {
            // Check for alternate group IDs
            if (dragData.group_id) {
              sidePanelData.conversationId = dragData.group_id;
            } else {
              // Check if this group has a different ID in the groupMessagesConversationsData
              const groupInfo = dataStore.groupMessagesData?.group_messages?.find(
                (g: { id: string; group_id?: string }) => g.id === dragData.id
              );
              if (groupInfo && groupInfo.group_id) {
                sidePanelData.conversationId = groupInfo.group_id;
              }
            }
          }
          // Open the side panel with the conversation data and pass dataStore
          openSidePanel(sidePanelData, dataStore);
        }
        // Fallback to using itemData
        else {
          const sidePanelData = {
            type: 'conversation',
            messageId: '', // No specific message for sidebar items
            conversationId: dragData.itemData.id,
            conversationType:
              dragData.itemData.itemType === 'contact'
                ? 'contact'
                : dragData.itemData.itemType === 'group_message'
                  ? 'group'
                  : dragData.itemData.itemType === 'channel'
                    ? 'channel'
                    : dragData.itemData.itemType === 'contact_center'
                      ? 'contact_center'
                      : dragData.itemData.itemType,
          };
          // For group messages, check if we need to map the ID
          if (dragData.itemData.itemType === 'group_message') {
            // Check if this group has a different ID in the groupMessagesConversationsData
            const groupInfo = dataStore.groupMessagesData?.group_messages?.find(
              (g: { id: string; group_id?: string }) => g.id === dragData.itemData.id
            );
            if (groupInfo && groupInfo.group_id) {
              sidePanelData.conversationId = groupInfo.group_id;
            }
          }
          // Open the side panel with the conversation data and pass dataStore
          openSidePanel(sidePanelData, dataStore);
          simpleDebugLog.log(
            'Opened side panel for sidebar row item using itemData:',
            dragData,
            'converted to:',
            sidePanelData
          );
        }
      }
    } catch (error) {
      simpleDebugLog.error('Error parsing drag data:', error);
    }

    isDragging.value = false;
  };

  // Global drag event listeners
  const handleDragEnter = (e: DragEvent) => {
    dragCounter++;

    // Check if this is a drag operation we should show the drop zone for
    if (e.dataTransfer && e.dataTransfer.types.includes('text/plain')) {
      // Check if we're dragging groups - groups can't be dropped in the side panel
      const isDraggingGroup = document.querySelector('.dragging-group') !== null;
      if (isDraggingGroup) {
        return; // Don't show global drop zone for group dragging
      }

      // Check if we're dragging from the sidebar
      const isDraggingFromSidebar = document.body.classList.contains('dragging-active');

      // Try to get the draggable element by using closest on the target
      // Include all known draggable elements in the selector
      const isDraggableItem = (e.target as Element)?.closest(
        '.dp3-feed--header-draggable, .dp3-inbox-feed-item-row[draggable="true"]'
      );

      // For inbox items, we may need to check the event target directly
      const target = e.target as Element;
      const isInboxItem =
        target.matches('[draggable="true"]') ||
        target.parentElement?.matches('[draggable="true"]') ||
        target.closest('.dragging') !== null;

      // Check if we have a dragging element anywhere in the document
      const hasDraggingElement = document.querySelector('.dragging') !== null;

      // Show the drop zone if it's from any valid draggable source
      if (isDraggingFromSidebar || isDraggableItem || isInboxItem || hasDraggingElement) {
        simpleDebugLog.log('Showing global drop zone for drag operation');
        isDragging.value = true;

        // If side panel is closed, open it partially
        if (!showSidePanel.value) {
          openSidePanelPartially();
        }
      }
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    dragCounter--;
    if (dragCounter === 0) {
      isDragging.value = false;
      setDragHovering(false);
      closeSidePanelPartially();
    }
  };

  const handleDrop = (e: DragEvent) => {
    dragCounter = 0;
    isDragging.value = false;
    setDragHovering(false);
    closeSidePanelPartially();
  };

  const handleDragEnd = (e: DragEvent) => {
    dragCounter = 0;
    isDragging.value = false;
    setDragHovering(false);
    closeSidePanelPartially();
  };

  // Setup and cleanup
  const setupGlobalDragListeners = () => {
    document.addEventListener('dragenter', handleDragEnter, true);
    document.addEventListener('dragleave', handleDragLeave, true);
    document.addEventListener('drop', handleDrop, true);
    document.addEventListener('dragend', handleDragEnd, true);
  };

  const removeGlobalDragListeners = () => {
    document.removeEventListener('dragenter', handleDragEnter, true);
    document.removeEventListener('dragleave', handleDragLeave, true);
    document.removeEventListener('drop', handleDrop, true);
    document.removeEventListener('dragend', handleDragEnd, true);
  };

  return {
    isDragging,
    onDropZoneDragOver,
    onDropZoneDragEnter,
    onDropZoneDragLeave,
    onDropZoneDrop,
    setupGlobalDragListeners,
    removeGlobalDragListeners,
  };
}
